import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Send, CheckCircle2, AlertCircle, Loader2, MessageCircle, Smartphone, Save } from 'lucide-react';
import { toast } from 'sonner';
import { updateBotConfig, getBotConfig, getAdminOtpChannels, updateAdminOtpChannels } from '../../../lib/api';
import MessagingProviders from './MessagingProviders';

export default function BotConfigSection() {
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // {configured, bot_username, masked_token}
  const [loading, setLoading] = useState(true);

  // OTP channel toggles (per-tenant)
  const [channels, setChannels] = useState({ enabled: { telegram: true, whatsapp: true, sms: true }, configured: { telegram: false, whatsapp: false, sms: false } });
  const [chLoading, setChLoading] = useState(true);
  const [chSaving, setChSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try { setStatus(await getBotConfig()); }
    catch { setStatus({ configured: false }); }
    finally { setLoading(false); }
  };
  const reloadChannels = async () => {
    setChLoading(true);
    try { setChannels(await getAdminOtpChannels()); }
    catch { /* leave defaults */ }
    finally { setChLoading(false); }
  };
  useEffect(() => { reload(); reloadChannels(); }, []);

  const save = async () => {
    if (!token.trim()) return toast.error('Enter a bot token');
    setSaving(true);
    try {
      const res = await updateBotConfig(token.trim());
      toast.success(res?.bot_username ? `Bot connected: @${res.bot_username}` : 'Bot token saved');
      setToken('');
      reload();
      reloadChannels();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm('Clear the saved bot token? OTPs will fall back to the platform default bot.')) return;
    try {
      await updateBotConfig('');
      toast.success('Bot token cleared');
      reload();
      reloadChannels();
    } catch (e) { toast.error('Failed'); }
  };

  const toggleChannel = (key) => {
    setChannels((c) => ({ ...c, enabled: { ...c.enabled, [key]: !c.enabled?.[key] } }));
  };

  const saveChannels = async () => {
    setChSaving(true);
    try {
      const res = await updateAdminOtpChannels(channels.enabled);
      setChannels((c) => ({ ...c, enabled: res.enabled }));
      toast.success('OTP channel settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save channels');
    } finally { setChSaving(false); }
  };

  const CH_META = [
    { key: 'telegram', label: 'Telegram', icon: Send, tip: 'Users receive OTP via your Telegram bot (deep-link).' },
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, tip: 'Users receive OTP via WhatsApp Cloud API template.' },
    { key: 'sms', label: 'SMS', icon: Smartphone, tip: 'Users receive OTP via SMS (requires SMS provider setup).' },
  ];

  return (
    <div className="space-y-6" data-testid="botconfig-section">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2"><Send size={18} /> Telegram Bot (OTP Sender)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status badge */}
          {loading ? (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 flex items-center gap-2 text-zinc-400 text-sm">
              <Loader2 size={16} className="animate-spin" /> Checking bot status…
            </div>
          ) : status?.configured ? (
            <div className="rounded-lg border border-emerald-700/40 bg-emerald-500/10 p-3 flex items-center justify-between gap-3" data-testid="bot-status-connected">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0">
                  <CheckCircle2 size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-emerald-300 font-bold text-sm">Bot is added{status.bot_username ? <> — <span className="text-emerald-100">@{status.bot_username}</span></> : ''}</div>
                  <div className="text-zinc-400 text-[11px] font-mono truncate">Token: {status.masked_token}</div>
                </div>
              </div>
              <button onClick={clear} className="text-xs text-red-300 hover:text-red-200 underline shrink-0" data-testid="bot-clear-btn">Remove</button>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-700/40 bg-amber-500/5 p-3 flex items-center gap-2" data-testid="bot-status-not-configured">
              <AlertCircle size={16} className="text-amber-400 shrink-0" />
              <div className="text-amber-300 text-sm">No bot configured for this tenant — OTPs use the platform default bot.</div>
            </div>
          )}

          <div className="text-zinc-400 text-sm leading-relaxed">
            Create a bot using <a className="text-[color:var(--gold)] underline" href="https://t.me/BotFather" target="_blank" rel="noreferrer">@BotFather</a> and paste the token here.
            Your end-users will click the Telegram deep link during signup / password reset and receive their OTP from <em>your</em> bot.
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Bot Token</Label>
            <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="123456:ABC-DEF..." className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono" data-testid="bot-token-input" />
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[color:var(--gold)] text-black font-bold" data-testid="bot-token-save-btn">{saving ? 'Validating…' : 'Save & Validate'}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Per-Tenant OTP Channel Toggles */}
      <Card className="bg-zinc-900 border-zinc-800" data-testid="otp-channels-card">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <Send size={18} /> OTP Channels for this Site
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-zinc-400 text-sm leading-relaxed">
            Choose which channels your users can pick to receive their OTP.
            Disabled or unconfigured channels are hidden from the sign-up / password-reset modals.
          </div>

          {chLoading ? (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 flex items-center gap-2 text-zinc-400 text-sm">
              <Loader2 size={16} className="animate-spin" /> Loading channel settings…
            </div>
          ) : (
            <div className="space-y-2">
              {CH_META.map(({ key, label, icon: Icon, tip }) => {
                const isEnabled = !!channels.enabled?.[key];
                const isConfigured = !!channels.configured?.[key];
                return (
                  <div key={key} className={`flex items-start gap-3 p-3 rounded-lg border ${isEnabled && isConfigured ? 'border-emerald-700/40 bg-emerald-500/5' : 'border-zinc-800 bg-zinc-950'}`} data-testid={`otp-channel-row-${key}`}>
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${isEnabled && isConfigured ? 'bg-emerald-500/15 text-emerald-300' : 'bg-zinc-800 text-zinc-400'}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-zinc-100 font-bold text-sm">{label}</span>
                        {isConfigured ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-700/40">Configured</span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40">Not Configured</span>
                        )}
                      </div>
                      <div className="text-zinc-400 text-xs mt-0.5">{tip}</div>
                    </div>
                    {/* Toggle switch */}
                    <button
                      type="button"
                      onClick={() => toggleChannel(key)}
                      role="switch"
                      aria-checked={isEnabled}
                      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${isEnabled ? 'bg-emerald-500' : 'bg-zinc-700'}`}
                      data-testid={`otp-channel-toggle-${key}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${isEnabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={saveChannels} disabled={chSaving || chLoading} className="bg-[color:var(--gold)] text-black font-bold" data-testid="otp-channels-save-btn">
              <Save size={14} className="mr-1.5" /> {chSaving ? 'Saving…' : 'Save Channels'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Per-Tenant WhatsApp + SMS Credentials */}
      <MessagingProviders onSaved={reloadChannels} />
    </div>
  );
}
